import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useFilterTenders } from '@/lib/api/hooks/tender';
import { Loader } from '@/components/ui/Loader';
import DashboardCard from '@/pages/dashboard/DashboardCard';

interface RecentTenderItem {
	id: string;
	title: string;
	date: string;
	author: string;
	deadline: string;
}

const RecentTenders = () => {
	const navigate = useNavigate();
	const { data: tendersData, isLoading, error } = useFilterTenders(
		{ page: 1, size: 3 },
		{ status: 'PUBLISH' }
	);

	if (error) {
		console.error('Error fetching tenders:', error);
	}

	const recentTenders = useMemo<RecentTenderItem[]>(() => {
		if (error || !tendersData?.data?.data) return [];
		const tenderList = Array.isArray(tendersData.data.data) ? tendersData.data.data : [];

		return tenderList.map((tender: { tenderId: string; tenderTitle: string; datePublished?: string; createdAt: string; username?: string; endDate: string }) => ({
			id: tender.tenderId,
			title: tender.tenderTitle,
			date: new Date(tender.datePublished || tender.createdAt).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
			author: tender.username || 'Admin',
			deadline: new Date(tender.endDate).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
		}));
	}, [tendersData, error]);

	return (
		<DashboardCard
			title="Recent Tenders"
			titleIcon={<KeyRound className="h-4 w-4 text-gray-500" />}
			headerAction={
				<Link to="/admin/tenders" className="text-sm font-semibold text-[#681628] hover:underline">
					View all
				</Link>
			}
		>
			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader size="md" variant="wine" />
				</div>
			) : recentTenders.length > 0 ? (
				<div className="space-y-3">
					{recentTenders.map((tender: RecentTenderItem, index: number) => (
						<div
							key={tender.id}
							className={`flex items-start justify-between gap-3 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors ${index < recentTenders.length - 1 ? 'pb-3 border-b border-gray-100' : ''}`}
							onClick={() => navigate(`/admin/tenders/${tender.id}`)}
						>
							<div className="flex-1 min-w-0">
								<h3 className="font-semibold text-gray-800 text-xs mb-0.5 line-clamp-2">{tender.title}</h3>
								<p className="text-xs text-gray-500">{tender.date} | {tender.author}</p>
							</div>
							<div className="text-right flex-shrink-0 min-w-[100px]">
								<p className="text-xs text-gray-500 mb-0.5">Deadline</p>
								<p className="text-xs font-semibold text-gray-900">{tender.deadline}</p>
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="text-center py-8">
					<p className="text-sm text-gray-500">No recent tenders</p>
				</div>
			)}
		</DashboardCard>
	);
};

export default RecentTenders;